import { mutate, type Arguments } from "swr";
import type { ApiResourceRoute } from "../resources/types";
import type { ClientType } from "../routing/clientType";
import { generateRoute } from "../routing/resolveRoute";
import { isApiGetKey } from "./apiKey";
import {
  matchesExactApiGetRoute,
  matchesBatchApiGetCache,
  matchesResourceCollectionCache,
} from "./matchers";
import {
  revalidateServerPagedCaches,
  type ServerPagedCacheRegistration,
} from "./serverPagedCacheRegistry";

function matchesRoute(route: string | readonly string[], targetRoute: string) {
  if (typeof route === "string") return route.startsWith(targetRoute);
  return route.some((item) => item.startsWith(targetRoute));
}

function matchesApiRouteFamily(
  key: Arguments,
  route: string,
  clientType?: ClientType,
): boolean {
  if (!isApiGetKey(key)) return false;
  if (clientType == null) return matchesRoute(key.route, route);
  if (key.clientType !== clientType) return false;

  const requestedRoute = generateRoute(route, clientType);
  if (!requestedRoute) return false;
  return matchesRoute(key.requestedRoute, requestedRoute);
}

function matchesRegisteredRouteFamily(
  registration: ServerPagedCacheRegistration,
  route: string,
  clientType?: ClientType,
) {
  if (clientType != null && registration.clientType !== clientType) {
    return false;
  }

  return registration.route.startsWith(route);
}

export async function revalidateApiCollection(resourceRoute: ApiResourceRoute) {
  const { collectionRoute, descriptor, route } = resourceRoute;
  if (collectionRoute == null) return;

  const requestedRoute = generateRoute(route, descriptor.clientType);
  if (!requestedRoute) return;

  const createMatcher = (namespace: "get" | "pagedGet") => (key: unknown) => {
    const matchesEntry = (entry: unknown) =>
      matchesResourceCollectionCache(entry, {
        descriptor,
        collectionRoute,
        requestedCollectionRoute: requestedRoute,
        namespace,
        clientType: descriptor.clientType,
      });
    return matchesEntry(key) || matchesBatchApiGetCache(key, matchesEntry);
  };

  await Promise.all([
    mutate(createMatcher("get")),
    mutate(createMatcher("pagedGet")),
    revalidateServerPagedCaches(
      (registration) =>
        route === collectionRoute &&
        registration.cacheTag === descriptor.cacheTag &&
        registration.clientType === descriptor.clientType &&
        registration.collectionRoute === collectionRoute,
    ),
  ]);
}

export async function revalidateApiGet(resourceRoute: ApiResourceRoute) {
  await revalidateApiGetRoute(
    resourceRoute.route,
    resourceRoute.descriptor.clientType,
  );
}

export async function revalidateApiGetRoute(
  route: string,
  clientType: ClientType,
) {
  await mutate(
    (key) =>
      matchesExactApiGetRoute(key, route, clientType) ||
      matchesBatchApiGetCache(key, (entry) =>
        matchesExactApiGetRoute(entry, route, clientType),
      ),
  );
}

export async function revalidateAllApiRoutes(
  route: string,
  clientType?: ClientType,
) {
  await Promise.all([
    mutate(
      (key) =>
        isApiGetKey(key) &&
        key.namespace !== "serverPagedGet" &&
        matchesApiRouteFamily(key, route, clientType),
    ),
    revalidateServerPagedCaches((registration) =>
      matchesRegisteredRouteFamily(registration, route, clientType),
    ),
  ]);
}

export async function revalidateAllApiResourceRoutes(
  resourceRoute: ApiResourceRoute,
) {
  await revalidateAllApiRoutes(
    resourceRoute.route,
    resourceRoute.descriptor.clientType,
  );
}

export function matchesApiRoute(
  key: Arguments,
  route: string,
  clientType?: ClientType,
) {
  return matchesApiRouteFamily(key, route, clientType);
}
